import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useData } from "../../lib/DataContext";
import { Qty } from "../shared";
import SearchSelect from "../SearchSelect";
import AutocompleteInput from "../AutocompleteInput";
import ResourceFields, { useMachines, resFromBom, resBody } from "../ResourceFields";
import FactSheetVendorFields from "./FactSheetVendorFields";
import { L, inputCls, NUM, baseCost, confirmExtSku, extSkuClashes } from "./common";
import FactSheetPolicyBlock from "./FactSheetPolicyBlock";
import FactSheetCostingTab, { ManualCost } from "./FactSheetCostingTab";

export default function FactSheetParamsTab({ it, pol, onSaved, onDeleted }) {
  const { items, itemsById, boms, suppliers } = useData();
  const init = () => ({ ...it, order_unit_qty: it.order_unit_qty ?? 1, base_unit_cost: baseCost(it) });
  const [f, setF] = useState(init);
  useEffect(() => setF(init()), [it.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF({ ...f, [k]: v });
  const num = (k, label, hint) => <L label={label} hint={hint}><input type="number" step="any" data-testid={`fact-${k}`} className={`${inputCls} font-mono`} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></L>;
  const txt = (k, label, hint) => <L label={label} hint={hint}><input data-testid={`fact-${k}`} className={inputCls} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></L>;
  const bom = boms.find((b) => b.product_id === it.id);
  const uniq = (k) => [...new Set(items.map((i) => i[k]).filter(Boolean))].sort();
  const [prate, setPrate] = useState(bom?.piece_rate || 0);
  useEffect(() => setPrate(bom?.piece_rate || 0), [bom?.id, bom?.piece_rate]);
  const [res, setRes] = useState(resFromBom(bom));
  useEffect(() => setRes(resFromBom(bom)), [bom?.id, bom?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const machines = useMachines();
  const ext = f.production_type === "external";
  const ptField = ext ? (
    <L label="OUTSOURCED PIECE RATE ($ / UNIT)" hint={bom ? "Stored on the BOM; added to material cost" : "Create a BOM first"}>
      <input type="number" step="any" min="0" disabled={!bom} data-testid="fact-piece_rate" className={`${inputCls} font-mono disabled:opacity-40`} value={prate} onChange={(e) => setPrate(e.target.value)} />
    </L>
  ) : bom ? (
    <div className="sm:col-span-2"><ResourceFields res={res} setRes={setRes} machines={machines} /></div>
  ) : (
    <L label="PRODUCTION TIME PER UNIT (MINS)" hint="Create a BOM first">
      <input type="number" disabled data-testid="fact-production_time_minutes" className={`${inputCls} font-mono disabled:opacity-40`} value={0} readOnly />
    </L>
  );
  const sup = suppliers.find((s) => s.id === f.supplier_id);
  const clashes = extSkuClashes(items, { ...it, ...f });

  const save = async () => {
    const body = {};
    ["name", "sku", "category", "unit", "supplier_id", "storage_location", "shopify_sku", "external_sku", "override_cost_engine", "notes"].forEach((k) => { if ((f[k] ?? "") !== (it[k] ?? "")) body[k] = f[k] ?? ""; });
    NUM.forEach((k) => { if (f[k] !== it[k] && f[k] !== "" && f[k] !== null && f[k] !== undefined) body[k] = Number(f[k]); });
    if (it.tier === 1 && f.base_unit_cost !== "" && Number(f.base_unit_cost) !== baseCost(it)) body.base_unit_cost = Number(f.base_unit_cost);
    if ("supplier_id" in body && !body.supplier_id) body.supplier_id = "";
    if (!!f.policy_override !== !!it.policy_override) body.policy_override = !!f.policy_override;
    if (!!f.manual_cost_override !== !!it.manual_cost_override) body.manual_cost_override = !!f.manual_cost_override;
    if (!!f.lead_override !== !!it.lead_override) body.lead_override = !!f.lead_override;
    if (!!f.inactive !== !!it.inactive) body.inactive = !!f.inactive;
    if (it.tier > 1 && (f.production_type || "internal") !== (it.production_type || "internal")) body.production_type = f.production_type || "internal";
    const ptChanged = bom && (Number(prate || 0) !== Number(bom.piece_rate || 0) || JSON.stringify(resBody(res)) !== JSON.stringify(resBody(resFromBom(bom))));
    if (!Object.keys(body).length && !ptChanged) return toast.info("No changes");
    if (("external_sku" in body || "supplier_id" in body) && !confirmExtSku(items, { ...it, ...f })) return;
    try {
      if (Object.keys(body).length) await api.put(`/items/${it.id}`, body);
      if (ptChanged) await api.put(`/boms/${bom.id}`, { product_id: bom.product_id, name: bom.name, notes: bom.notes || "", components: bom.components, ...resBody(res), piece_rate: Number(prate) || 0 });
      toast.success("Item saved — forecast, ordering and schedule updated"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await api.delete(`/items/${it.id}`); toast.success("Deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {txt("name", "NAME")}{txt("sku", "SKU")}
        <L label="CATEGORY"><AutocompleteInput testId="fact-category" padX="px-3" className={inputCls} value={f.category || ""} onChange={(v) => set("category", v)} options={uniq("category")} /></L>
        <L label="UNIT"><AutocompleteInput testId="fact-unit" padX="px-3" className={inputCls} value={f.unit || ""} onChange={(v) => set("unit", v)} options={uniq("unit")} /></L>
        {num("par_level", "PAR LEVEL")}
        <label className={`flex items-center gap-2 text-sm self-end pb-3 cursor-pointer ${f.inactive ? "text-[#EF4444]" : ""}`} data-tip="Inactive items leave stock tracking, movement pickers, scheduling, ADCR and auto-purchasing. History, POs and stock logs are kept.">
          <input type="checkbox" data-testid="fact-inactive" className="w-5 h-5 accent-[#EF4444]" checked={!!f.inactive} onChange={(e) => set("inactive", e.target.checked)} />Inactive
        </label>
      </div>
      <FactSheetPolicyBlock f={f} set={set} pol={pol} />
      <div className="font-mono text-[10px] tracking-[0.2em] text-[#F59E0B] pt-2">{{ 1: "RAW MATERIAL PARAMETERS", 2: "PREPRODUCTION PARAMETERS", 3: "FINISHED GOOD PARAMETERS" }[it.tier]}</div>
      {it.tier > 1 && <FactSheetCostingTab it={it} ext={ext} f={f} set={set} pol={pol} onToggle={(v) => set("production_type", v ? "external" : "internal")} price={it.tier === 3 ? Number(f.selling_price) : 0} />}
      {it.tier === 1 && (
        <div className="grid grid-cols-2 gap-3" data-testid="fact-t1-fields">
          {num("order_unit_qty", "ORDER UNIT QTY", "Roll / case / pack size")}
          <div className="col-span-2"><ManualCost f={f} set={set} pol={pol} it={it} hint={`Updated on PO receipt (${pol?.cost_engine === "latest" ? "latest" : "weighted average"}); base and landed adjustment are tracked separately`} /></div>
          <div className="col-span-2"><FactSheetVendorFields it={it} f={f} set={set} suppliers={suppliers} sup={sup} /></div>
          <div className="col-span-2">{txt("external_sku", "EXTERNAL SKU (SUPPLIER PART #)", "Used on purchase orders · blank = internal SKU")}
            {clashes.length > 0 && <div data-testid="fact-ext-sku-dup-warning" className="mt-1 text-xs text-[#F59E0B]">Also used by {clashes.map((o) => o.name).join(", ")} from this supplier</div>}</div>
          <div className="col-span-2">{txt("storage_location", "STORAGE LOCATION / BIN")}</div>
        </div>
      )}
      {it.tier === 2 && (
        <div className="space-y-3" data-testid="fact-t2-fields">
          <div className="p-3 rounded-md border border-[#1E293B] text-sm">
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">PARENT MATERIAL LINKS (BOM)</div>
            {bom ? bom.components.map((c) => <div key={c.item_id} className="flex justify-between py-0.5"><span>{itemsById[c.item_id]?.name}</span><Qty v={c.qty} unit={itemsById[c.item_id]?.unit} /></div>) : <div className="text-[#6B7280]">No BOM linked. Create one on the BOMs page.</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {num("unit_yield", "SINGLE PASS YIELD", "Pieces produced per cutting cycle")}{num("batch_size", "BATCH RUN YIELD", "Forecast rounds builds up to this")}
            {num("setup_minutes", "SETUP TIME (MIN)")}{ptField}
            {num("max_daily_qty", "MAXIMUM DAILY PRODUCTION LIMIT", "Max units scheduled per day · excess spills to later days · 0 = no limit")}
            <div className="col-span-2">{txt("storage_location", "STORAGE LOCATION / BIN")}</div>
          </div>
        </div>
      )}
      {it.tier === 2 && ext && <FactSheetVendorFields it={it} f={f} set={set} suppliers={suppliers} sup={sup} outsourced />}
      {it.tier === 3 && (
        <div className="grid grid-cols-2 gap-3" data-testid="fact-t3-fields">
          {txt("shopify_sku", "SHOPIFY SKU", "Blank = matches internal SKU")}{num("selling_price", "SELLING PRICE ($)")}
          {num("assembly_minutes", "ASSEMBLY TIME (MIN / UNIT)", "Reference only")}{ptField}
          {num("max_daily_qty", "MAXIMUM DAILY PRODUCTION LIMIT", "Max units scheduled per day · excess spills to later days · 0 = no limit")}
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <button data-testid="fact-save-btn" onClick={save} className="flex-1 h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center justify-center gap-2"><Save className="w-4 h-4" />Save</button>
        <button data-testid="fact-delete-btn" onClick={del} className="h-12 w-12 rounded-md border border-[#EF4444]/40 text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
